import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { filtrarClientes, formatearDocumentoCliente } from "../../servicios/clientes";
import { puedeAdministrarClientes, puedeCrearClientes } from "../../servicios/sesionSeguridad";
import { useStorePos } from "./useStorePos";

const PosClientes = () => {
  const { t } = useTranslation();
  const [consulta, setConsulta] = useState("");
  const clientes = useStorePos((state) => state.ListaClientes);
  const cargando = useStorePos((state) => state.BuscandoClientes);
  const error = useStorePos((state) => state.ErrorClientes);
  const eliminandoId = useStorePos((state) => state.EliminandoClienteId);
  const consumidorFinalId = useStorePos((state) => state.ClienteConsumidorFinalId);
  const puedeCrear = puedeCrearClientes();
  const puedeAdministrar = puedeAdministrarClientes();

  const resultados = useMemo(
    () => filtrarClientes(clientes, consulta)
      .filter((cliente) => Number(cliente.Id) !== Number(consumidorFinalId)),
    [clientes, consulta, consumidorFinalId]
  );

  const darDeBaja = async (cliente) => {
    if (!puedeAdministrar) return;
    const confirmacion = await Swal.fire({
      icon: "warning",
      title: t("customers.deactivateCustomer"),
      text: t("customers.deactivateCustomerConfirm", { name: cliente.sDescripcion || t("customers.thisCustomer") }),
      showCancelButton: true,
      confirmButtonText: t("customers.deactivateCustomer"),
      cancelButtonText: t("common.cancel"),
      confirmButtonColor: "#b42318",
    });
    if (confirmacion.isConfirmed) {
      await useStorePos.getState().eliminarCliente(cliente);
    }
  };

  return (
    <section className="pos-pendientes pos-clientes" aria-labelledby="pos-clientes-titulo">
      <header className="pos-pendientes-header pos-clientes-encabezado">
        <div>
          <h3 id="pos-clientes-titulo" className="ing-subtitulo">{t("pos.customers")}</h3>
          <p className="pos-ayuda">{puedeAdministrar ? t("customers.manageHelp") : t("customers.queryAndCreateHelp")}</p>
        </div>
        {puedeCrear && <div className="pos-clientes-encabezado-acciones">
          <button
            type="button"
            className="pos-btn-principal"
            onClick={() => useStorePos.getState().abrirAltaCliente()}
          >
            <i className="pi pi-user-plus" /> {t("pos.newCustomer")}
          </button>
        </div>}
      </header>

      <div className="pos-clientes-panel">
        <div className="pos-clientes-herramientas">
          <label className="pos-clientes-busqueda">
            <i className="pi pi-search" aria-hidden="true" />
            <input
              type="search"
              value={consulta}
              onChange={(event) => setConsulta(event.target.value)}
              placeholder={t("customers.searchPlaceholder")}
              aria-label={t("customers.searchLabel")}
            />
          </label>
          <button
            type="button"
            className="pos-btn-secundario"
            onClick={() => useStorePos.getState().getClientes({ forzar: true })}
            disabled={cargando}
          >
            <i className={`pi ${cargando ? "pi-spin pi-spinner" : "pi-refresh"}`} />
            {t("common.refresh")}
          </button>
        </div>

        {error && (
          <div className="pos-clientes-estado error" role="alert">
            <i className="pi pi-exclamation-circle" /> {error}
          </div>
        )}
        {!error && cargando && (
          <div className="pos-clientes-estado">
            <i className="pi pi-spin pi-spinner" /> {t("customers.loadingCustomers")}
          </div>
        )}
        {!error && !cargando && resultados.length === 0 && (
          <div className="pos-clientes-estado">
            <i className="pi pi-users" />
            {consulta ? t("customers.noSearchMatches") : t("customers.noCustomers")}
          </div>
        )}
        {!error && !cargando && resultados.length > 0 && (
          <div className="pos-ticket-table-wrap pos-clientes-tabla">
            <table className="pos-ticket-table">
              <thead>
                <tr>
                  <th>{t("common.code")}</th>
                  <th>{t("pos.customer")}</th>
                  <th>{t("pos.document")}</th>
                  <th>{t("pos.vatCondition")}</th>
                  <th>{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {resultados.map((cliente) => {
                  const documentoFormateado = formatearDocumentoCliente(cliente);
                  const documento = [cliente.sTipoDocumento, documentoFormateado]
                    .filter(Boolean)
                    .join(" ") || "—";
                  const condicion = cliente.sCondicionIVA || "—";
                  const eliminando = Number(eliminandoId) === Number(cliente.Id);
                  return (
                    <tr key={cliente.Id}>
                      <td data-label={t("common.code")}><strong>{cliente.sCodigo || "—"}</strong></td>
                      <td data-label={t("pos.customer")}>{cliente.sDescripcion || t("customers.withoutName")}</td>
                      <td data-label={t("pos.document")}>{documento}</td>
                      <td data-label={t("pos.vatCondition")}>{condicion}</td>
                      <td data-label={t("common.actions")} className="pos-clientes-acciones">
                        {puedeAdministrar ? <>
                        <button
                          type="button"
                          onClick={() => useStorePos.getState().abrirEdicionCliente(cliente)}
                          disabled={Boolean(eliminandoId)}
                        >
                          <i className="pi pi-pencil" /> {t("common.edit")}
                        </button>
                        <button
                          type="button"
                          className="baja"
                          onClick={() => darDeBaja(cliente)}
                          disabled={Boolean(eliminandoId)}
                        >
                          <i className={`pi ${eliminando ? "pi-spin pi-spinner" : "pi-trash"}`} />
                          {eliminando ? t("customers.deactivating") : t("customers.deactivateCustomer")}
                        </button>
                        </> : <span>{t("customers.readOnly")}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default PosClientes;
